import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, CheckCircle2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { LOAN_PRODUCTS } from "@/lib/loanProducts";

const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const productTitle = (key) => LOAN_PRODUCTS.find((p) => p.key === key)?.title || key;

export default function AdminLoanDetail() {
  const { id } = useParams();
  const [loan, setLoan] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/loans/${id}`).then(({ data }) => setLoan(data));
  useEffect(() => { load(); }, [id]);

  const markPaid = async (n) => {
    setBusy(true);
    try {
      await api.post(`/loans/${id}/installments/${n}/pay`, {});
      toast.success(`Installment ${n} marked paid`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!loan) return <DashboardLayout role="admin"><div>Loading…</div></DashboardLayout>;
  const pct = Math.min(100, (loan.amount_paid / loan.total_repayable) * 100);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <DashboardLayout role="admin">
      <Link to="/admin/loans" className="mb-4 inline-flex items-center gap-2 text-sm text-ink-soft hover:text-[#5B2C82]" data-testid="loan-back">
        <ArrowLeft className="h-4 w-4" /> All loans
      </Link>

      <div className="rounded-xl border border-[#E5E2DE] bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-ink">{loan.applicant_name}</h1>
            <div className="text-xs text-ink-muted">{loan.applicant_email} · {productTitle(loan.product)}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/loans/${loan.id}/agreement.pdf?auth=${localStorage.getItem("jrc_token") || ""}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#5B2C82] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#46225f]"
              data-testid="admin-download-agreement"
            >
              <FileDown className="h-3.5 w-3.5" /> Download agreement
            </a>
            <span className={`rounded-full border px-3 py-1 text-xs ${loan.status === "active" ? "border-emerald-300 bg-emerald-100 text-emerald-900" : "border-violet-300 bg-violet-100 text-violet-900"}`}>{loan.status}</span>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Principal" value={fmt(loan.principal)} />
          <Stat label="Rate" value={`${loan.interest_rate_pct}%`} />
          <Stat label="Term" value={`${loan.term_months} mo`} />
          <Stat label="Total repayable" value={fmt(loan.total_repayable)} />
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-ink">{fmt(loan.amount_paid)} collected</span>
            <span className="text-ink-soft">{fmt(loan.balance)} outstanding</span>
          </div>
          <Progress value={pct} className="mt-2 h-2" />
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-[#E5E2DE] bg-white">
        <div className="border-b border-[#E5E2DE] px-5 py-3">
          <h2 className="font-heading text-lg font-semibold text-ink">Repayment schedule</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F6F0] text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Due</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right">Balance after</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loan.schedule.map((inst) => {
                const overdue = inst.status !== "paid" && inst.due_date < today;
                return (
                  <tr key={inst.installment_no} className="border-t border-[#E5E2DE]" data-testid={`admin-sched-row-${inst.installment_no}`}>
                    <td className="px-4 py-3 text-ink-muted">{inst.installment_no}</td>
                    <td className="px-4 py-3">{inst.due_date}</td>
                    <td className="px-4 py-3 text-right font-semibold">{fmt(inst.amount_due)}</td>
                    <td className="px-4 py-3 text-right text-ink-soft">{fmt(inst.balance_after)}</td>
                    <td className="px-4 py-3">
                      {inst.status === "paid" ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-900">Paid {inst.paid_on}</span>
                      ) : overdue ? (
                        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs text-rose-900">Overdue</span>
                      ) : (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {inst.status !== "paid" && (
                        <Button size="sm" variant="outline" onClick={() => markPaid(inst.installment_no)} disabled={busy} data-testid={`mark-paid-${inst.installment_no}`}>
                          <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Mark paid
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-0.5 font-heading text-base font-bold text-ink">{value}</div>
    </div>
  );
}
